<script setup lang="ts">
import { ref, useAttrs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { FILTER_MESSAGES, EMPTY_MESSAGES } from '@Constants';
import type { DropdownSettings, DropdownOptions, SelectInputNumber } from '@FilterModule';

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits<{
  'update:modelValue': [filter: string];
}>();

const attrs = useAttrs();
const { t } = useI18n();

const inputModel = ref();
const dropdownModel = ref();

const dropdownFocused = ref(false);
const placeholder = ref('');

function onBlur() {
  dropdownFocused.value = false;
  placeholder.value = '';
}

function onFocus() {
  dropdownFocused.value = true;
  placeholder.value = t(attrs.placeholder as string);
}

watch(inputModel, (newInput, oldInput) => updateModel(newInput, oldInput));

watch(
  () => dropdownModel.value,
  (newDropdown, oldDropdown) => updateModel(newDropdown, oldDropdown),
  { deep: true }
);

watch(
  () => attrs.modelValue as string,
  (newValue) => updateInternalModels(newValue),
  { deep: true }
);

function updateModel(newModel?: number | DropdownOptions, oldModel?: number | DropdownOptions) {
  if (newModel === oldModel) return;
  if (!inputModel.value || !dropdownModel.value?.value) return;

  emit('update:modelValue', `${inputModel.value}|${dropdownModel.value?.value}`);
}

function updateInternalModels(newModel?: string) {
  if (!newModel || (Array.isArray(newModel) && newModel.length <= 0)) {
    inputModel.value = 0;
    dropdownModel.value = undefined;
    return;
  }

  const [newInput, newDropdown] = newModel.split('|');
  const dropdownAttrs = attrs.dropdown as DropdownSettings;

  inputModel.value = parseInt(newInput);
  dropdownModel.value = dropdownAttrs.options.filter(({ value }) => value === newDropdown)[0];
}
</script>

<template>
  <section class="flex">
    <FloatLabel
      v-tooltip.left="attrs.hint ? $t(attrs.hint as string) : ''"
      class="mt-2 w-full"
    >
      <Dropdown
        v-bind="{ ...(attrs.dropdown as DropdownSettings), FILTER_MESSAGES, EMPTY_MESSAGES }"
        v-model="dropdownModel"
        class="w-3/4"
        pt:emptyMessage="text-sm"
        pt:filterInput="h-9 py-1 pl-3 text-sm"
        pt:input="flex h-9 items-center p-1 pl-3 pr-7 text-sm text-gray-500"
        pt:item="text-sm"
        pt:root="w-full"
        @focus="onFocus"
        @blur="onBlur"
      >
        <template #value="slotProps">
          {{ slotProps.value ? $t(slotProps.value.name) : placeholder }}
        </template>

        <template #option="slotProps">
          {{ $t(slotProps.option.name) }}
        </template>
      </Dropdown>

      <label
        v-if="attrs.label"
        class="-mt-3 flex items-center gap-2 text-sm text-zinc-500"
      >
        <div class="hidden sm:block">
          <FaIcon
            v-if="attrs.hint"
            aria-hidden
            class="size-3 text-gray-500"
            :icon="$icons.faCircleInfo"
          />
        </div>

        <span>{{ $t(attrs.label as string) }}</span>
      </label>
    </FloatLabel>

    <InputNumber
      :disabled="!dropdownModel"
      v-model="inputModel"
      v-bind="attrs.inputNumber as SelectInputNumber"
      class="mt-2 h-9 w-20 pl-3"
      pt:input:root="w-full text-sm text-zinc-500 flex items-center flex"
    />
  </section>
</template>
